import type { UTCDate } from '@date-fns/utc';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';
import type { SelectionList, ValidValueTypes } from '@revelio/iso-utility';

import type { FilterDataResult } from '../../contexts/FilterProvider';
import type {
  Filter,
  FilterId,
  FreeTextFilter,
  ListFilter,
  RangeFilter,
  SchoolFilter,
  StaticListFilter,
  TreeFilter,
} from '../../types/filters';
import {
  flattenUserNodeToAtoms,
  splitExprIntoTopLevelAndConjuncts,
} from './expr';
import { mergeInstancesByFilter } from './instances';
import { toMultiOrSingle } from './multi';
import { resolveSchoolNamesInPlace } from './school';
import type { CompanyRics } from './selection';
import { transformAiDateRangeToCustomDashFilter } from './transformDateRange';
import { transformRangeLeavesInConjunct } from './transformRange';
import { transformRootStaticLists } from './transformRootStaticLists';
import { transformTextLeavesInConjunct } from './transformText';
import { transformValueLeavesInConjunct } from './transformValue';
import type { AiTransformUnknownFilter } from './types';

/**
 * Transforms the AI v2 filter response into CustomDash filter objects (expr tree plus
 * top-level non-TD fields such as `date_range`).
 *
 * ### Terminology used in this folder
 * - **top-level clause**: a `{ expr, isNegated }` pair extracted
 *   from the outer `UserNode` tree. Think: "one top-level `user_value.child` expression",
 *   plus whether it was wrapped in `user_not`.
 *
 *   Example (pseudo):
 *   - `user_and([ user_value(A), user_value(B) ])` → atoms: [A, B]
 *   - `user_not(user_value(A))` → atoms: [{ expr: A, isNegated: true }]
 *
 * - **Conjunct**: one top-level AND component
 *   inside an `ExprNode`.
 *   We split `A AND B AND (C OR D)` into: [A, B, (C OR D)].
 *
 *   Why: we treat each top-level AND conjunct as its own “chip group” so multi-filters can
 *   represent AND semantics via multiple instances.
 *
 *   Example (roles):
 *   - `(role=Engineer OR role=Scientist)` → single instance with 2 selections (OR)
 *   - `role=Engineer AND role=Scientist` → 2 instances in MULTI_ROLE (AND)
 *
 * Notes:
 * - We do **not** split through NOT, because `NOT (A AND B)` cannot be represented as two
 *   independent chips without changing meaning (De Morgan).
 * - They can look "the same" when there is no top-level AND:
 *   - one atom whose `expr` is `A OR B` → conjuncts is just `[A OR B]` (single item).
 */
const transformAiResponseV2ToCustomDashFilters = ({
  response,
  selectionLists,
  getFilterData,
  systemMaxDate,
}: {
  response: TalentDiscoveryAiFilterSearchResponseV2;
  selectionLists: SelectionList<ValidValueTypes>[];
  getFilterData: (filterId: FilterId) => FilterDataResult;
  /** Lag-aware latest data date, for preset date-range re-resolution. */
  systemMaxDate?: UTCDate | null;
}): {
  filters: Filter[];
  unknownFilters: AiTransformUnknownFilter[];
} => {
  // Step 1: flatten the outer `UserNode` structure into top-level clauses:
  // each clause is an `ExprNode` plus a boolean that tells us if it was wrapped in `user_not`.
  const topLevelClauses = flattenUserNodeToAtoms(response.filters.expr);

  const treeInstancesByFilter = new Map<FilterId, TreeFilter[]>();
  const listInstancesByFilter = new Map<FilterId, ListFilter[]>();
  const staticInstancesByFilter = new Map<FilterId, StaticListFilter[]>();
  const rangeInstancesByFilter = new Map<FilterId, RangeFilter[]>();
  const freeTextInstancesByFilter = new Map<FilterId, FreeTextFilter[]>();
  const schoolInstancesByFilter = new Map<FilterId, SchoolFilter[]>();

  const unknownFilters: AiTransformUnknownFilter[] = [];

  const companyNameByRcid = new Map<number, string>(
    (response.filters.companyNames ?? []).map((entry) => [
      entry.rcid,
      entry.name,
    ])
  );

  // RICS ancestry per AI-resolved company, used to build the path-encoded
  // company id (`${rics_k10}_${rics_k400}_${rcid}`) so the adaptive taxonomy
  // resolves natively from the company filter — same as a company-search pick.
  const companyRicsByRcid = new Map<number, CompanyRics>(
    (response.filters.companyNames ?? []).map((entry) => [
      entry.rcid,
      {
        rics_k10: entry.rics_k10 ?? null,
        rics_k50: entry.rics_k50 ?? null,
        rics_k400: entry.rics_k400 ?? null,
      },
    ])
  );

  for (const clause of topLevelClauses) {
    // Step 2: split each clause into top-level AND groups ("conjuncts").
    // One clause can produce multiple groups:
    // - clause expr: (Role=Engineer OR Role=Scientist) AND Company=OpenAI
    // - groups:      [(Role=Engineer OR Role=Scientist), Company=OpenAI]
    const andGroups = splitExprIntoTopLevelAndConjuncts(clause.expr);

    for (const conjunct of andGroups) {
      // Treat each top-level AND conjunct as its own "chip group".
      // This preserves AND semantics by producing multiple instances for multi-filters.
      const groupTreeInstances = new Map<FilterId, TreeFilter[]>();
      const groupListInstances = new Map<FilterId, ListFilter[]>();
      const groupStaticInstances = new Map<FilterId, StaticListFilter[]>();
      const groupRangeInstances = new Map<FilterId, RangeFilter[]>();
      const groupFreeTextInstances = new Map<FilterId, FreeTextFilter[]>();
      const groupSchoolInstances = new Map<FilterId, SchoolFilter[]>();

      transformRangeLeavesInConjunct({
        conjunct,
        isNegated: clause.isNegated,
        groupRangeInstances,
      });

      transformTextLeavesInConjunct({
        conjunct,
        isNegated: clause.isNegated,
        groupFreeTextInstances,
        globalFreeTextInstances: freeTextInstancesByFilter,
      });

      transformValueLeavesInConjunct({
        conjunct,
        isNegated: clause.isNegated,
        selectionLists,
        getFilterData,
        unknownFilters,
        groupTreeInstances,
        groupListInstances,
        groupStaticInstances,
        groupSchoolInstances,
        companyNameByRcid,
        companyRicsByRcid,
      });

      // Append this group's instances so multi-filters preserve AND semantics between groups.
      mergeInstancesByFilter(treeInstancesByFilter, groupTreeInstances);
      mergeInstancesByFilter(listInstancesByFilter, groupListInstances);
      mergeInstancesByFilter(staticInstancesByFilter, groupStaticInstances);
      mergeInstancesByFilter(rangeInstancesByFilter, groupRangeInstances);
      mergeInstancesByFilter(freeTextInstancesByFilter, groupFreeTextInstances);
      mergeInstancesByFilter(schoolInstancesByFilter, groupSchoolInstances);
    }
  }

  // Step 3: best-effort "decorations" based on extra response payload.
  resolveSchoolNamesInPlace({
    schoolInstancesByFilter,
    schoolNames: response.filters.schoolNames,
  });

  // Step 4: build final filters.
  // Each `Map<FilterId, FilterInstance[]>` becomes either:
  // - a single filter, or
  // - a MULTI_* wrapper containing multiple instances (when needed).
  const finalFilters: Filter[] = [];

  treeInstancesByFilter.forEach((instances, filterId) => {
    finalFilters.push(...toMultiOrSingle(filterId, instances));
  });
  listInstancesByFilter.forEach((instances, filterId) => {
    finalFilters.push(...toMultiOrSingle(filterId, instances));
  });
  staticInstancesByFilter.forEach((instances, filterId) => {
    finalFilters.push(...toMultiOrSingle(filterId, instances));
  });
  rangeInstancesByFilter.forEach((instances, filterId) => {
    finalFilters.push(...toMultiOrSingle(filterId, instances));
  });
  freeTextInstancesByFilter.forEach((instances, filterId) => {
    finalFilters.push(...toMultiOrSingle(filterId, instances));
  });
  schoolInstancesByFilter.forEach((instances, filterId) => {
    finalFilters.push(...toMultiOrSingle(filterId, instances));
  });

  const dateRangeFilter = transformAiDateRangeToCustomDashFilter(
    response.date_range,
    systemMaxDate
  );
  if (dateRangeFilter) {
    finalFilters.push(dateRangeFilter);
  }

  // `posting_source` and `overall_rating` sit at the artifact ROOT beside
  // `date_range`, not in `expr`, so the leaf walk above never sees them.
  finalFilters.push(...transformRootStaticLists({ response }));

  return { filters: finalFilters, unknownFilters };
};

export const transformAiResponseToCustomDashFilters = (
  response: TalentDiscoveryAiFilterSearchResponseV2,
  selectionLists: SelectionList<ValidValueTypes>[],
  getFilterData: (filterId: FilterId) => FilterDataResult,
  /**
   * The account's lag-aware latest data date. A preset date_range
   * ('last-year', …) is re-resolved against THIS max — a lagged trial's
   * "last year" must anchor where its data actually ends, not at the global
   * max the server resolved against. Omit (index still loading) to apply the
   * server-resolved dates.
   */
  systemMaxDate?: UTCDate | null
): {
  filters: Filter[];
  unknownFilters: AiTransformUnknownFilter[];
} => {
  return transformAiResponseV2ToCustomDashFilters({
    response,
    selectionLists,
    getFilterData,
    systemMaxDate,
  });
};
